import React from 'react';
import { PostInsights } from '@frontend/gql/social/graphql';
import { useSocialMediaThumbnail } from '@frontend/applications/Social/hooks/useSocialMediaThumbnail';
import {
  CardActionButtons,
  CardUsernameDisplay,
  CardBadgeSection,
  CardStatsSection,
} from '@frontend/shadcn/components/card-overlay';
import {
  useContentPreviewActionButtons,
  useCardUsernameDisplay,
  useContentPreviewBadges,
  useContentPreviewStats,
} from '@frontend/shadcn/components/card-overlay/hooks';
import { useTagsDisplay } from '@/shadcn/components/content-preview/hooks/use-tags-display';
import { TagsDisplay } from '@/shadcn/components/content-preview/tags-display';
import {
  ContentPreviewCard as ShadcnContentPreviewCard,
  ContentPreviewCardMedia,
  ContentPreviewCardSkeleton,
  ContentPreviewCardBackground,
  ContentPreviewCardContent,
  ContentPreviewCardImageContainer,
  ContentPreviewCardImageContent,
  ContentPreviewCardImage,
  ContentPreviewCardPlaceholder,
  ContentPreviewCardGradient,
  ContentPreviewCardFooter,
} from '@/shadcn/components/content-preview';
import type { SocialNetwork } from '@/applications/Social/types/social-network';

type ContentPreviewCardProps = {
  post: PostInsights;
  className?: string;
  onDownload?: () => void;
  onOpenExternal?: () => void;
  showDownload?: boolean;
};

/**
 * Platform mapping configuration
 */
const PLATFORM_BY_NETWORK: Record<string, SocialNetwork> = {
  instagram: 'instagram',
  twitter: 'twitter',
  x: 'twitter',
  facebook: 'facebook',
  tiktok: 'tiktok',
  youtube: 'youtube',
  pinterest: 'pinterest',
} as const;

/**
 * Maps entityNetwork to platform for username display
 *
 * @param entityNetwork - The entity network string from PostInsights
 * @returns The platform type for username display
 */
export function mapEntityNetworkToPlatform(entityNetwork?: string): SocialNetwork {
  const key = (entityNetwork || '').toLowerCase();
  return PLATFORM_BY_NETWORK[key];
}

const ContentPreviewCard = React.forwardRef<HTMLDivElement, ContentPreviewCardProps>(
  ({ post, className, onDownload, onOpenExternal, showDownload = true, ...props }, ref) => {
    const total = post.total;
    const { tags, tagsVisible } = useTagsDisplay(post.postCaption);
    const { imageUrl, isLoading, onImageError } = useSocialMediaThumbnail({
      postUrl: post.postPermalink ? post.postPermalink : '',
      postImageUrl: post.postImageUrl ? post.postImageUrl : '',
    });

    const showLoading = isLoading;
    const showImage = imageUrl && !showLoading;
    const showPlaceholder = !showImage && !showLoading;

    // Get username for display
    const username = post.accountUsername || post.member?.instagramUsername;

    // Check if boost eligible
    const isBoostEligible = post.advertisableMediaPermissions?.some((permission) => permission?.hasPermissions);

    // Use hooks to build component props
    const { cardActionButtonsProps, hasActions } = useContentPreviewActionButtons(
      onDownload,
      onOpenExternal,
      showDownload,
    );

    // Map entityNetwork to platform and use the appropriate hook
    const platform = mapEntityNetworkToPlatform(post.entityNetwork);
    const { cardUsernameDisplayProps, shouldDisplay: shouldDisplayUsername } = useCardUsernameDisplay({
      username,
      platform,
      maxWidth: 'max-w-[calc(100%-4.5rem)]', // No tooltip truncation needed for content preview
    });

    const { cardBadgeSectionProps, hasBadges } = useContentPreviewBadges(isBoostEligible);

    const { cardStatsSectionProps, hasStats } = useContentPreviewStats(
      post.postDate,
      total?.impressions,
      total?.engagements,
    );

    return (
      <ShadcnContentPreviewCard ref={ref} className={className} {...props}>
        <ContentPreviewCardMedia>
          {showLoading && <ContentPreviewCardSkeleton />}
          {!showLoading && !showPlaceholder && imageUrl && (
            <>
              <ContentPreviewCardBackground imageUrl={imageUrl} />
              <ContentPreviewCardImageContainer>
                <ContentPreviewCardImageContent>
                  <ContentPreviewCardImage src={imageUrl} onError={onImageError} />
                </ContentPreviewCardImageContent>
              </ContentPreviewCardImageContainer>
            </>
          )}
          {showPlaceholder && <ContentPreviewCardPlaceholder />}
          <ContentPreviewCardGradient />
        </ContentPreviewCardMedia>

        <ContentPreviewCardContent>
          {/* Top Action Buttons */}
          {hasActions && <CardActionButtons {...cardActionButtonsProps} />}

          {/* Username Display */}
          {shouldDisplayUsername && cardUsernameDisplayProps && <CardUsernameDisplay {...cardUsernameDisplayProps} />}

          <div className="flex-grow" />

          <ContentPreviewCardFooter>
            {/* Boost Eligible Badge */}
            {hasBadges && <CardBadgeSection {...cardBadgeSectionProps} />}

            {/* Tags Row - only show if post has caption */}
            <TagsDisplay tags={tags} tagsVisible={tagsVisible} />

            {/* Date and Stats Row */}
            {hasStats && <CardStatsSection {...cardStatsSectionProps} />}
          </ContentPreviewCardFooter>
        </ContentPreviewCardContent>
      </ShadcnContentPreviewCard>
    );
  },
);

ContentPreviewCard.displayName = 'ContentPreviewCard';

export { ContentPreviewCard };
